import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, Field, G_DARK, Input, PasswordInput } from "../../components/Shared";
import { rememberFocus } from "../../focus";
import MailTemplates from "./MailTemplates";
import { SuccessNote } from "./parts";

// Management -> E-mail: the SMTP relay that notification e-mails go through
// (GET/PATCH /api/admin/mail, POST /api/admin/mail/probe, POST /api/admin/mail/test).
// The same pattern as StorageCard:
// - the password is write-only (`password_set`); blank keeps the stored one, and a new host or
//   port, or security None, needs it typed again (the server answers 422 otherwise);
// - saving connects, signs in and says NOOP first; a refusal is the server's sentence, shown
//   verbatim, and nothing is saved. A blank host saves without a check and turns e-mail off.
// Below it, MailTemplates.jsx: the editable template of every automatic e-mail.

const URL = "/admin/mail";
const TEXT = ["host", "security", "username", "from_address"];
const SECURITY = [
  { value: "starttls", label: "STARTTLS (usually port 587)" },
  { value: "tls", label: "TLS from the start (usually port 465)" },
  { value: "none", label: "None (a relay on a trusted network only)" },
];

function formFrom(c) {
  return { host: c.host ?? "", port: String(c.port ?? 587), security: c.security || "starttls",
           username: c.username ?? "", from_address: c.from_address ?? "", password: "" };
}

export function mailChanges(form, stored) {
  const before = formFrom(stored);
  const out = {};
  for (const k of TEXT) {
    const v = form[k].trim();
    if (v !== before[k]) out[k] = v;
  }
  const port = form.port.trim();
  if (port !== before.port) out.port = /^\d+$/.test(port) ? Number(port) : port;
  if (form.password) out.password = form.password;
  return out;
}

export default function MailCard() {
  const [cfg, setCfg] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(null);           // null | "save" | "probe" | "test"
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [result, setResult] = useState(null);
  const [testTo, setTestTo] = useState("");
  const buttonsRef = useRef(null);

  useEffect(() => {
    let live = true;
    api.get(URL)
      .then(c => { if (live) { setCfg(c); setForm(formFrom(c)); } })
      .catch(e => { if (live) setLoadError(e); });
    return () => { live = false; };
  }, []);

  if (loadError) return <ErrorNote error={loadError} />;
  if (!cfg) return <Loading />;

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const run = async (kind, fn) => {
    setError(null);
    setNotice(null);
    setResult(null);
    const restore = rememberFocus();
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      if (kind === "save") setError(e);
      else setResult({ ok: false, detail: e.message });
    } finally {
      setBusy(null);
      restore(() => buttonsRef.current && buttonsRef.current.querySelector("button"));
    }
  };

  const save = () => run("save", async () => {
    const payload = mailChanges(form, cfg);
    if (Object.keys(payload).length === 0) { setNotice("Nothing has changed."); return; }
    const updated = await api.patch(URL, payload);
    setCfg(updated);
    setForm(formFrom(updated));
    setNotice(updated.host ? "Saved. The relay answered and accepted the sign-in."
                           : "Saved. E-mail is off: notifications stay in the portal.");
  });

  const check = () => run("probe", async () => {
    const r = await api.post(`${URL}/probe`, mailChanges(form, cfg));
    setResult({ ok: !!r?.ok, detail: r?.detail || "" });
  });

  const sendTest = () => run("test", async () => {
    const r = await api.post(`${URL}/test`, testTo.trim() ? { to: testTo.trim() } : {});
    setResult({ ok: !!r?.ok, detail: r?.detail || "" });
  });

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>E-mail</h2>
        <Badge color={cfg.configured ? "green" : "gray"}>
          <span aria-hidden="true" style={{ marginRight: 4 }}>{cfg.configured ? "✓" : "○"}</span>
          {cfg.configured ? "Configured" : "Not configured"}
        </Badge>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#4b5563" }}>
        Notifications (builds, imports, migrations, data check errors) always show in the portal's
        bell. People who choose e-mail for a kind also get it through this SMTP relay; a message
        that cannot be sent is tried again for two days.
      </p>

      <ErrorNote error={error} />
      <SuccessNote>{notice}</SuccessNote>

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        <Field label="SMTP host" hint="e.g. smtp.example.org. Blank turns e-mail off.">
          <Input value={form.host} onChange={set("host")} spellCheck={false} />
        </Field>
        <Field label="Port">
          <Input value={form.port} onChange={set("port")} inputMode="numeric" />
        </Field>
        <Field label="Security">
          <select value={form.security} onChange={set("security")}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, fontFamily: "inherit", background: "#fff" }}>
            {SECURITY.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Sender address" hint="The From of every message, e.g. unio@example.org.">
          <Input value={form.from_address} onChange={set("from_address")} spellCheck={false} />
        </Field>
        <Field label="User name" hint="Blank when the relay needs no sign-in.">
          <Input value={form.username} onChange={set("username")} spellCheck={false} autoComplete="off" />
        </Field>
        <Field label="Password" hint={cfg.password_set ? "Currently set — leave blank to keep it" : "Not set"}>
          <PasswordInput value={form.password} onChange={set("password")} autoComplete="new-password"
                         placeholder={cfg.password_set ? "•••••••• (unchanged)" : ""} />
        </Field>
      </div>

      <div ref={buttonsRef} style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Btn onClick={save} disabled={!!busy}>{busy === "save" ? "Checking the relay…" : "Save"}</Btn>
        <Btn variant="secondary" onClick={check} disabled={!!busy}>
          {busy === "probe" ? "Checking the relay…" : "Check without saving"}
        </Btn>
      </div>
      <p style={{ margin: "8px 0 0", fontSize: 12, color: "#4b5563" }}>
        Saving connects and signs in first: a mistyped host would otherwise be found only when
        nobody gets their e-mail.
      </p>

      <h3 style={{ margin: "20px 0 4px", fontSize: 15, fontWeight: 700 }}>Test message</h3>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px" }}>
          <Field label="Send a test message to" hint="Blank = your own address.">
            <Input value={testTo} onChange={e => setTestTo(e.target.value)} spellCheck={false} />
          </Field>
        </div>
        <Btn variant="secondary" onClick={sendTest} disabled={!!busy || !cfg.configured}>
          {busy === "test" ? "Sending…" : "Send test message"}
        </Btn>
      </div>
      {result && (
        <div role="status" style={{ marginTop: 10, fontSize: 13, color: result.ok ? G_DARK : "#b91c1c" }}>
          <span aria-hidden="true">{result.ok ? "✓ " : "✗ "}</span>
          {result.detail}
        </div>
      )}

      <MailTemplates configured={cfg.configured} />
    </Card>
  );
}
